"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "~/components/v3/shadcn/Sheet";
import { Button } from "~/components/v4/themed/Button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "~/components/v3/shadcn/Form";
import {
    duplicateEvent,
    GetEventFromSlug,
    removeRecurrence,
    setRecurrence,
} from "../actions";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/shadcn/Select";
import {
    FaArrowLeft,
    FaChevronRight,
    FaMinus,
    FaPlus,
    FaRegClock,
} from "react-icons/fa";
import { LuGlobe, LuLock, LuX } from "react-icons/lu";
import { getEventImageUrl } from "~/utils/images";
import { TimePicker } from "~/components/v3/shadcn/TimePicker";
import { DatePicker } from "~/components/v3/shadcn/DatePicker";
import { TimezoneSelect } from "~/components/v3/shadcn/Timezone";
import { DateTime } from "luxon";
import { cn } from "~/utils/fontsConfig";

import { Frequency, RRule, Weekday } from "rrule";
import { toastHandler } from "~/components/ToastHandler";
import Link from "next/link";
import { EventImage } from "~/components/v3/events-components/EventImage";
import { EventImageType } from "~/types/EventTypes";
import { RiMapPinAddFill } from "react-icons/ri";
import { cities } from "~/utils/cities";
import { MultiSelectV2 } from "~/components/shadcn/MultiSelectV2";
import { Switch } from "~/components/shadcn/Switch";
import SimulcastLabelTooltip from "./SimulcastLabelTooltip";

const recurrEventSchema = z.object({
    recurrence: z
        .object({
            interval: z.number(),
            freq: z.enum(["YEARLY", "MONTHLY", "WEEKLY"]),
            weekdays: z.array(z.string()),
            weekdayPos: z.number().optional(),
        })
        .optional(),
});

const cloneEventSchema = z.object({
    startDates: z.array(
        z.object({
            date: z.date(),
            time: z.string(),
        }),
    ),
    timezone: z.string(),
    visibility: z.string(),
    cities: z.array(z.string()),
    isSimulcast: z.boolean().optional(),
});

type RecurrenceFormValues = z.infer<typeof recurrEventSchema>;
type CloneEventFormValues = z.infer<typeof cloneEventSchema>;

type Props = {
    isOpen: boolean;
    onClose: () => void;
    event: GetEventFromSlug;
    defaultMode?: "recurrence" | "duplication";
};

const EventRecurrenceSheet = ({
    isOpen,
    onClose,
    event,
    defaultMode = "duplication",
}: Props) => {
    const [mode, setMode] = useState<"recurrence" | "duplication">(defaultMode);

    useEffect(() => {
        setMode(defaultMode);
    }, [defaultMode]);

    return (
        <Sheet open={isOpen} onOpenChange={onClose}>
            <SheetContent
                showCloseIcon={false}
                className="flex h-full w-full flex-col overflow-y-auto rounded-br-md rounded-tr-md border-none bg-v3-N600 p-4 md:min-w-[500px] md:p-6"
            >
                {mode === "duplication" ? (
                    <DuplicateContent
                        event={event}
                        onClose={onClose}
                        setMode={setMode}
                    />
                ) : (
                    <RecurrenceContent
                        event={event}
                        onClose={onClose}
                        setMode={setMode}
                    />
                )}
            </SheetContent>
        </Sheet>
    );
};

type ClonedEventType = EventImageType & {
    startTime: Date;
    name: string;
    slug: string;
    timezone: string;
};

const DuplicateContent = ({
    event,
    onClose,
    setMode,
}: {
    event: GetEventFromSlug;
    onClose: () => void;
    setMode: (mode: "recurrence" | "duplication") => void;
}) => {
    const startTime = DateTime.fromJSDate(event.startTime, {
        zone: "UTC",
    }).setZone("local", { keepLocalTime: true });
    const [clonedEvents, setClonedEvents] = useState<ClonedEventType[]>([]);
    const cityOptions = cities.map((c) => {
        return { label: c.cityLong, value: c.citySlug };
    });

    const form = useForm<CloneEventFormValues>({
        resolver: zodResolver(cloneEventSchema),
        defaultValues: {
            startDates: [
                {
                    date: DateTime.now().plus({ days: 1 }).toJSDate(),
                    time: startTime.toFormat("HH:mm"),
                },
            ],
            timezone: event.timezone,
            visibility: "Public",
            cities: [],
        },
    });
    const startDates = form.watch("startDates");

    function onSubmit(data: CloneEventFormValues) {
        // This would be connected to actual mutation logic in the future
        console.log("Form submitted:", data);
        toastHandler({
            successMessage: "Event duplicated",
            loadingMessage: "Duplicating event...",
            errorMessage: "Failed to duplicate event",
            operation: async () => {
                const events = await duplicateEvent({
                    ...data,
                    startDates: data.startDates.map((d) =>
                        DateTime.fromJSDate(d.date)
                            .set({
                                hour: parseInt(d.time.split(":")[0] ?? "0"),
                                minute: parseInt(d.time.split(":")[1] ?? "0"),
                            })
                            .setZone("UTC", { keepLocalTime: true })
                            .toJSDate(),
                    ),
                    eventSlug: event.slug,
                });
                setClonedEvents(events);
            },
        });
    }

    const handleAddDate = () => {
        const startDates = form.watch("startDates");
        form.setValue("startDates", [
            ...startDates,
            {
                date: DateTime.fromJSDate(
                    startDates[startDates.length - 1]!.date,
                )
                    .plus({ weeks: startDates.length + 1 })
                    .toJSDate(),
                time: startTime.toFormat("HH:mm"),
            },
        ]);
    };

    const handleRemoveDate = () => {
        const startDates = form.watch("startDates");
        form.setValue("startDates", startDates.slice(0, -1));
    };

    const selectedCities = form.watch("cities");

    if (clonedEvents.length > 0) {
        return (
            <>
                <SheetHeader className="flex-none py-2">
                    <div className="flex w-full items-center justify-between">
                        <SheetTitle className="text-lg">
                            Event Duplication
                        </SheetTitle>
                        <SheetClose className="focus:ring-ring rounded-sm opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                            <LuX className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </SheetClose>
                    </div>
                </SheetHeader>
                <div className="py-2 text-xs">
                    <p>
                        Event has been duplicated. You can find the links to new
                        events below.
                    </p>
                </div>
                <ClonedEventsList events={clonedEvents} />
            </>
        );
    }

    return (
        <>
            <SheetHeader className="flex-none py-2">
                <div className="flex w-full items-center justify-between">
                    <SheetTitle className="text-lg">
                        Event Duplication
                    </SheetTitle>
                    <SheetClose className="focus:ring-ring rounded-sm opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                        <LuX className="h-5 w-5" />
                        <span className="sr-only">Close</span>
                    </SheetClose>
                </div>
            </SheetHeader>

            <div className="py-2 text-xs">
                <p>
                    You are duplicating following event with the same
                    information. Everything except the guest list will be copied
                    over.
                </p>
            </div>

            <div className="flex flex-row items-center">
                {event.image && (
                    <div>
                        <Image
                            src={getEventImageUrl(event)}
                            alt={event.name}
                            width={56}
                            height={52}
                            className="mx-auto mt-1 rounded-md bg-gray-300 object-cover will-change-transform group-hover:scale-[1.01]"
                            quality={100}
                            sizes="56px"
                            placeholder="blur"
                            blurDataURL={getEventImageUrl(event)}
                            priority
                        />
                    </div>
                )}
                <div className="ml-4 font-medium">{event.name}</div>
            </div>

            <Form {...form}>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-6"
                >
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <h3 className="text-sm font-medium">Start time</h3>

                            {startDates.map((startDate, index) => (
                                <div
                                    className="grid grid-cols-2 gap-2"
                                    key={index}
                                >
                                    <FormField
                                        control={form.control}
                                        name={`startDates.${index}.date`}
                                        render={({ field }) => (
                                            <FormItem className="flex-1 space-y-2">
                                                <FormControl>
                                                    <DatePicker
                                                        value={field.value}
                                                        onChange={(date) => {
                                                            field.onChange(
                                                                date,
                                                            );
                                                        }}
                                                        disabled={{
                                                            before: new Date(),
                                                        }}
                                                        className="rounded-r-none"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name={`startDates.${index}.time`}
                                        render={({ field }) => (
                                            <FormItem className="flex-1 space-y-2">
                                                <FormControl>
                                                    <TimePicker
                                                        value={field.value}
                                                        onChange={(time) => {
                                                            field.onChange(
                                                                time,
                                                            );
                                                        }}
                                                        className="rounded-l-none"
                                                    ></TimePicker>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center space-x-2">
                            <Button
                                variant="default"
                                size="xs"
                                type="button"
                                onClick={handleAddDate}
                                disabled={form.watch("cities").length > 0}
                                className="rounded-lg bg-white/15 p-3 text-xs text-white"
                            >
                                <FaPlus className="h-4 w-4" />
                                <span>Add date</span>
                            </Button>

                            {startDates.length > 1 && (
                                <Button
                                    variant="default"
                                    size="xs"
                                    type="button"
                                    onClick={handleRemoveDate}
                                    className="rounded-lg bg-white/15 p-3 text-xs text-white"
                                >
                                    <FaMinus className="h-4 w-4" />
                                    <span>Remove date</span>
                                </Button>
                            )}

                            <FormField
                                control={form.control}
                                name="cities"
                                render={({ field }) => (
                                    <MultiSelectV2
                                        className="w-fit"
                                        options={cityOptions}
                                        onChange={field.onChange}
                                        hasSelectAll={true}
                                    >
                                        <Button
                                            variant="default"
                                            size="xs"
                                            type="button"
                                            disabled={startDates.length > 1}
                                            className="rounded-lg bg-white/15 p-3 text-xs text-white"
                                        >
                                            <RiMapPinAddFill className="h-4 w-4" />
                                            <span>
                                                {field.value.length
                                                    ? "Edit cities"
                                                    : "Add cities"}
                                            </span>
                                        </Button>
                                    </MultiSelectV2>
                                )}
                            />
                            {form.watch("cities").length > 0 && (
                                <div className="!ml-auto">
                                    <FormField
                                        control={form.control}
                                        name="isSimulcast"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl className="flex items-center">
                                                    <div className="flex items-center gap-2">
                                                        <Switch
                                                            checked={
                                                                field.value
                                                            }
                                                            className="h-[18px] w-[34px] data-[state=checked]:bg-v3-clover"
                                                            thumbClassName="h-3 w-3 data-[state=checked]:translate-x-4 bg-white"
                                                            onCheckedChange={
                                                                field.onChange
                                                            }
                                                        />
                                                        <FormLabel className="text-xs">
                                                            <SimulcastLabelTooltip />
                                                        </FormLabel>
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                        {selectedCities.length > 0 && (
                            <div>
                                <div className="flex flex-row flex-wrap gap-2">
                                    {selectedCities.slice(0, 5).map((city) => (
                                        <div
                                            key={city}
                                            className="whitespace-nowrap rounded-lg bg-v3-N500 px-2 py-1 text-xs text-v3-N300"
                                        >
                                            {
                                                cities.find(
                                                    (c) => c.citySlug === city,
                                                )?.cityLong
                                            }
                                        </div>
                                    ))}
                                    {selectedCities.length > 5 && (
                                        <div className="whitespace-nowrap rounded-lg bg-v3-N500 px-2 py-1 text-xs text-v3-N300">
                                            and {selectedCities.length - 5}{" "}
                                            {selectedCities.length - 5 === 1
                                                ? "other"
                                                : "others"}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div>
                            <FormField
                                control={form.control}
                                name="timezone"
                                render={({ field }) => (
                                    <FormItem className="flex w-full flex-col gap-2">
                                        <FormLabel className="text-sm font-[300] text-v3-N300">
                                            Timezone
                                        </FormLabel>
                                        <FormControl>
                                            <TimezoneSelect
                                                disabled={
                                                    !!form.watch("cities")
                                                        .length &&
                                                    !form.watch("isSimulcast")
                                                }
                                                value={field.value}
                                                onChange={field.onChange}
                                                className="rounded-[0.625rem] tracking-wide"
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div>
                            <FormField
                                control={form.control}
                                name="visibility"
                                render={({ field }) => (
                                    <FormItem className="flex w-full flex-col gap-2">
                                        <FormLabel className="text-sm font-[300] text-v3-N300">
                                            Visibility
                                        </FormLabel>
                                        <FormControl>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <SelectTrigger className="rounded-[0.625rem] p-2 px-3">
                                                    <SelectValue>
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="flex justify-center rounded-full bg-v3-clover/20 p-[6px]"
                                                                aria-label="Visibility"
                                                            >
                                                                {field.value ===
                                                                "Public" ? (
                                                                    <LuGlobe className="text-lg text-v3-clover" />
                                                                ) : (
                                                                    <LuLock className="text-lg text-v3-clover" />
                                                                )}
                                                            </span>
                                                            <span>
                                                                {field.value}
                                                            </span>
                                                        </div>
                                                    </SelectValue>
                                                </SelectTrigger>

                                                <SelectContent position="item-aligned">
                                                    <SelectItem value="Public">
                                                        Public
                                                    </SelectItem>
                                                    <SelectItem value="Private">
                                                        Private
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    <Button
                        type="submit"
                        // make it so this button is positioned in bottom right
                        className="absolute bottom-20 right-4"
                    >
                        Duplicate
                    </Button>
                </form>
            </Form>
        </>
    );
};

const ClonedEventsList = ({ events }: { events: ClonedEventType[] }) => {
    return (
        <div className="flex flex-col gap-4 py-4">
            <h3 className="text-sm font-medium text-v3-N300">
                Duplicated Events
            </h3>
            <div className="flex flex-col gap-2">
                {events.map((event) => (
                    <Link
                        key={event.slug}
                        href={`/events/${event.slug}`}
                        target="_blank"
                        className="flex items-center gap-3 rounded-lg bg-v3-N500 p-3 transition-colors hover:bg-v3-N400"
                    >
                        {event.image && (
                            <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-md">
                                <EventImage
                                    event={event}
                                    width={200}
                                    height={150}
                                    className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                                    alt={event.name}
                                    options={{
                                        type: "cover",
                                    }}
                                    isReadonly
                                />
                            </div>
                        )}
                        {!event.image && (
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-white/10">
                                <span className="text-lg text-white">
                                    {event.name.charAt(0)}
                                </span>
                            </div>
                        )}
                        <div className="flex flex-col">
                            <span className="truncate text-sm font-medium text-white">
                                {event.name}
                            </span>
                            <span className="text-xs text-v3-N300">
                                {DateTime.fromJSDate(event.startTime, {
                                    zone: "UTC",
                                })
                                    .setZone(event.timezone, {
                                        keepLocalTime: true,
                                    })
                                    .toFormat("EEE, dd MMM, HH:mm ZZZZ")}
                            </span>
                        </div>
                        <FaChevronRight className="ml-auto text-v3-N300" />
                    </Link>
                ))}
            </div>
        </div>
    );
};

const toRRule = (str: string) => {
    switch (str) {
        case "WEEKLY":
            return RRule.WEEKLY;
        case "MONTHLY":
            return RRule.MONTHLY;
        case "YEARLY":
            return RRule.YEARLY;
        default:
            throw new Error("Unable to convert into recurring rule");
    }
};

const weekdaysToByWeekday = (weekdays: string[]) => {
    return weekdays.map((day) => {
        switch (day) {
            case "Mon":
                return RRule.MO;
            case "Tue":
                return RRule.TU;
            case "Wed":
                return RRule.WE;
            case "Thu":
                return RRule.TH;
            case "Fri":
                return RRule.FR;
            case "Sat":
                return RRule.SA;
            case "Sun":
                return RRule.SU;
            default:
                throw new Error("Unable to convert into recurring rule ");
        }
    });
};

const freqToString = (freq: Frequency | undefined) => {
    switch (freq) {
        case RRule.WEEKLY:
            return "WEEKLY";
        case RRule.MONTHLY:
            return "MONTHLY";
        case RRule.YEARLY:
            return "YEARLY";
        default:
            return null;
    }
};

const weekdaysToString = (weekdays: number[] | undefined) => {
    if (!weekdays?.length) return [];
    return weekdays?.map((day) => {
        switch (day) {
            case 0:
                return "Mon";
            case 1:
                return "Tue";
            case 2:
                return "Wed";
            case 3:
                return "Thu";
            case 4:
                return "Fri";
            case 5:
                return "Sat";
            case 6:
                return "Sun";
            default:
                return undefined;
        }
    });
};

const RecurrenceContent = ({
    event,
    onClose,
    setMode,
}: {
    event: GetEventFromSlug;
    onClose: () => void;
    setMode: (mode: "recurrence" | "duplication") => void;
}) => {
    const initialRRule = event.recurrenceRule
        ? RRule.fromString(event.recurrenceRule)
        : undefined;

    const form = useForm<RecurrenceFormValues>({
        resolver: zodResolver(recurrEventSchema),
        defaultValues: {
            recurrence: {
                interval: initialRRule?.options.interval ?? 1,
                freq: freqToString(initialRRule?.options.freq) ?? "MONTHLY",
                weekdays: weekdaysToString(initialRRule?.options.byweekday),
                weekdayPos: initialRRule?.options.bysetpos?.[0] ?? undefined,
            },
        },
    });
    const [rrule, setRrule] = useState<RRule | undefined>(initialRRule);

    useEffect(() => {
        const startTime = DateTime.fromJSDate(event.startTime, {
            zone: "UTC",
        }).setZone("local", { keepLocalTime: true });
        const weekdays = form.watch("recurrence.weekdays") ?? [];
        const rrule = new RRule({
            freq: toRRule(form.watch("recurrence.freq") as any as string),
            interval: form.watch("recurrence.interval"),
            byweekday: weekdays.length
                ? weekdaysToByWeekday(weekdays)
                : undefined,
            bysetpos: weekdays.length
                ? form.watch("recurrence.weekdayPos")
                : undefined,
            dtstart: startTime.toJSDate(),
        });
        setRrule(rrule);
        // setPreviewDates(
        //     getPreviewDates(
        //         startTime,
        //         rrule
        //             .all(function (date, i) {
        //                 return i < 5;
        //             })
        //             .map((date) => DateTime.fromJSDate(date)),
        //     ),
        // );
    }, [
        form.watch("recurrence.weekdays"),
        form.watch("recurrence.weekdayPos"),
        form.watch("recurrence.interval"),
        form.watch("recurrence.freq"),
        event.startTime,
    ]);

    const freqToLabel = (
        freq: "YEARLY" | "MONTHLY" | "WEEKLY",
        interval: number = 1,
    ) => {
        let suffix = "";
        if (interval > 1) suffix = `s`;
        if (freq === "WEEKLY") return `week${suffix}`;
        if (freq === "MONTHLY") return `month${suffix}`;
        if (freq === "YEARLY") return `year${suffix}`;
    };

    function onSubmit(data: RecurrenceFormValues) {
        if (!rrule) return;
        toastHandler({
            successMessage: "Recurrence set",
            loadingMessage: "Setting recurrence...",
            errorMessage: "Failed to set recurrence",
            operation: async () => {
                console.log("Form w/ recurrence set:", data);
                await setRecurrence({
                    eventSlug: event.slug,
                    rrule: rrule.toString(),
                });
                onClose();
            },
        });
    }

    const handleRemoveRecurrence = () => {
        toastHandler({
            successMessage: "Recurrence removed",
            loadingMessage: "Removing recurrence...",
            errorMessage: "Failed to remove recurrence",
            operation: async () => {
                await removeRecurrence({
                    eventSlug: event.slug,
                });
                onClose();
            },
        });
    };

    const recurrenceWeekdays = form.watch("recurrence.weekdays") ?? [];

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <SheetHeader className="flex-none py-2">
                    <div className="flex w-full items-center justify-between">
                        <SheetTitle className="flex items-center gap-4 text-lg">
                            <button onClick={() => setMode("duplication")}>
                                <FaArrowLeft />
                            </button>
                            Set Recurrence
                        </SheetTitle>
                        <SheetClose className="focus:ring-ring rounded-sm opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                            <LuX className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </SheetClose>
                    </div>
                </SheetHeader>

                <div className="flex flex-col space-y-6 py-4">
                    <div className="space-y-4">
                        <h3 className="text-sm font-medium text-v3-N300">
                            Repeat every
                        </h3>
                        <div className="flex items-center gap-2">
                            <NumberSelect
                                value={
                                    form.watch(
                                        "recurrence.interval",
                                    ) as any as number
                                }
                                onChange={(value) => {
                                    form.setValue("recurrence.interval", value);
                                }}
                                className="w-18 rounded-r-none"
                                values={[
                                    { value: 1, label: "1" },
                                    { value: 2, label: "2" },
                                    { value: 3, label: "3" },
                                    { value: 4, label: "4" },
                                    { value: 5, label: "5" },
                                    { value: 6, label: "6" },
                                    { value: 7, label: "7" },
                                ]}
                            />
                            <Select
                                value={form.watch("recurrence.freq")}
                                onValueChange={(value) => {
                                    form.setValue(
                                        "recurrence.freq",
                                        value as
                                            | "YEARLY"
                                            | "MONTHLY"
                                            | "WEEKLY",
                                    );
                                }}
                            >
                                <SelectTrigger className="rounded-[0.625rem] rounded-l-none p-2 px-3">
                                    <SelectValue>
                                        <div className="flex items-center gap-2">
                                            <span>
                                                <FaRegClock />
                                            </span>
                                            <span>
                                                {freqToLabel(
                                                    form.watch(
                                                        "recurrence.freq",
                                                    ) as any as
                                                        | "YEARLY"
                                                        | "MONTHLY"
                                                        | "WEEKLY",
                                                    form.watch(
                                                        "recurrence.interval",
                                                    ),
                                                )}
                                            </span>
                                        </div>
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent position="item-aligned">
                                    <SelectItem value="WEEKLY">Week</SelectItem>
                                    <SelectItem value="MONTHLY">
                                        Month
                                    </SelectItem>
                                    <SelectItem value="YEARLY">Year</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="text-sm font-medium text-v3-N300">
                            Weekdays{" "}
                            <span className="text-v3-N400">(optional)</span>
                        </h3>
                        <div className="flex justify-stretch gap-2">
                            {[
                                "Mon",
                                "Tue",
                                "Wed",
                                "Thu",
                                "Fri",
                                "Sat",
                                "Sun",
                            ].map((day, i) => (
                                <Button
                                    key={day}
                                    variant="secondary"
                                    size="sm"
                                    type="button"
                                    className={cn(
                                        "rounded-fxll h-8 w-full p-0",
                                        "bg-v3-N500 text-white",
                                        "hover:bg-white hover:text-black disabled:bg-v3-N-200 disabled:text-v3-N-300",
                                        recurrenceWeekdays.includes(day) &&
                                            "bg-white text-black",
                                    )}
                                    onClick={() => {
                                        const recurrenceWeekdays =
                                            form.watch("recurrence.weekdays") ||
                                            [];
                                        if (recurrenceWeekdays.includes(day)) {
                                            form.setValue(
                                                "recurrence.weekdays",
                                                recurrenceWeekdays.filter(
                                                    (d) => d !== day,
                                                ),
                                            );
                                            return;
                                        }
                                        form.setValue("recurrence.weekdays", [
                                            ...recurrenceWeekdays,
                                            day,
                                        ]);
                                    }}
                                >
                                    {day}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {recurrenceWeekdays.length > 0 && (
                        <div className="flex items-center gap-4">
                            <h3 className="text-sm font-medium">
                                Repeat every
                            </h3>
                            <NumberSelect
                                value={form.watch("recurrence.weekdayPos") ?? 1}
                                onChange={(value) => {
                                    form.setValue(
                                        "recurrence.weekdayPos",
                                        value,
                                    );
                                }}
                                className="w-18"
                                values={[
                                    { value: -2, label: "second-last" },
                                    { value: -1, label: "last" },
                                    { value: 1, label: "1st" },
                                    { value: 2, label: "2nd" },
                                    { value: 3, label: "3rd" },
                                    { value: 4, label: "4th" },
                                    { value: 5, label: "5th" },
                                ]}
                            />
                            <div className="flex justify-stretch gap-2">
                                {form.watch("recurrence.weekdays")?.join(", ")}{" "}
                                in given{" "}
                                {freqToLabel(
                                    form.watch("recurrence.freq") as any as
                                        | "YEARLY"
                                        | "MONTHLY"
                                        | "WEEKLY",
                                )}
                            </div>
                        </div>
                    )}

                    {rrule && (
                        <RecurrencePreview
                            dates={rrule
                                .all(function (date, i) {
                                    return i < 5;
                                })
                                .map((date) => DateTime.fromJSDate(date))}
                            eventName={event.name}
                            eventStartTime={event.startTime}
                        />
                    )}

                    <div className="sticky bottom-5 right-0 flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => handleRemoveRecurrence()}
                            disabled={!event.recurrenceRule}
                        >
                            Remove Recurrence
                        </Button>
                        <Button type="submit">Set Recurrence</Button>
                    </div>
                </div>
            </form>
        </Form>
    );
};

const getPreviewDates = (startTime: DateTime, dates: DateTime[]) => {
    const groupedByMonth: Record<string, DateTime[]> = {};
    dates.forEach((date) => {
        const monthKey = date.toFormat("MMMM yyyy");
        if (!groupedByMonth[monthKey]) {
            groupedByMonth[monthKey] = [];
        }
        // lock the time to the original event time
        groupedByMonth[monthKey].push(
            date.set({
                hour: startTime.hour,
                minute: startTime.minute,
            }),
        );
    });

    return groupedByMonth;
};

export const RecurrencePreview = ({
    dates,
    eventName,
    eventStartTime,
}: {
    dates: DateTime[];
    eventName: string;
    eventStartTime: Date;
}) => {
    const startTime = DateTime.fromJSDate(eventStartTime, {
        zone: "UTC",
    }).setZone("local", { keepLocalTime: true });
    const previewDates: Record<string, DateTime[]> = getPreviewDates(
        startTime,
        dates,
    );

    return (
        <div className="space-y-4">
            <h3 className="text-sm font-medium text-v3-N300">
                Preview of upcoming recurring events
            </h3>
            <div className="flex flex-col gap-6">
                {Object.entries(previewDates).map(([month, dates], index) => (
                    <div key={index} className="flex flex-col gap-2">
                        <span className="text-sm uppercase">{month}</span>
                        {dates.map((date, index) => (
                            <div
                                key={date.toMillis()}
                                className="flex items-center gap-3 rounded-lg bg-v3-N500 p-3"
                            >
                                <div className="flex h-10 w-10 flex-shrink-0 flex-col items-center justify-center rounded-md bg-white/10">
                                    <span className="text-xs text-v3-N200">
                                        {date.toFormat("ccc")}
                                    </span>
                                    <span className="text-sm">
                                        {date.toFormat("d")}
                                    </span>
                                </div>
                                <div className="flex flex-col overflow-hidden">
                                    <span className="truncate text-sm font-medium text-white">
                                        {eventName}
                                    </span>
                                    <span className="flex items-center gap-1 text-xs text-v3-N300">
                                        <FaRegClock className="text-v3-N300" />
                                        {date.toFormat("t")}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

const NumberSelect = ({
    value,
    onChange,
    values,
    className,
}: {
    value: number;
    onChange: (value: number) => void;
    values: { value: number; label: string }[];
    className?: string;
}) => {
    return (
        <Select
            value={(value ?? "").toString()}
            onValueChange={(value) => onChange(parseInt(value))}
        >
            <SelectTrigger
                className={cn("rounded-[0.625rem] p-2 px-3", className)}
            >
                <SelectValue>
                    <div className="flex items-center gap-2 px-4">
                        <span>
                            {values.find((v) => v.value === value)?.label}
                        </span>
                    </div>
                </SelectValue>
            </SelectTrigger>
            <SelectContent position="item-aligned">
                {values.map(({ value, label }) => (
                    <SelectItem key={value} value={value.toString()}>
                        {label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

export default EventRecurrenceSheet;
